import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  authApi,
  decodeJwtPayload,
  isTokenExpired,
  type LoginResponse,
  type ResetPasswordResponse,
  type SetupMfaResponse,
  type ConfirmMfaResponse,
  type ForgotPasswordResponse,
  type ConfirmForgotPasswordResponse,
} from "@repo/api";
import { Logger } from "@saviynt/common";

interface AuthContextType {
  isAuthenticated: boolean;
  isAuthChecked: boolean;
  isLoading: boolean;
  userEmail: string | null;
  completeAuth: () => void;
  login: (username: string, password: string) => Promise<LoginResponse>;
  verifyMfa: (
    username: string,
    mfaCode: string,
    session: string,
    challengeName: string,
  ) => Promise<LoginResponse>;
  resetPassword: (
    username: string,
    newPassword: string,
    session: string,
  ) => Promise<ResetPasswordResponse>;
  setupMfa: (username: string, session: string) => Promise<SetupMfaResponse>;
  confirmMfa: (
    username: string,
    mfaCode: string,
    session: string,
  ) => Promise<ConfirmMfaResponse>;
  forgotPassword: (username: string) => Promise<ForgotPasswordResponse>;
  confirmForgotPassword: (
    username: string,
    confirmationCode: string,
    newPassword: string,
  ) => Promise<ConfirmForgotPasswordResponse>;
  loginWithSSO: (code: string, state: string) => Promise<LoginResponse>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const USERNAME_KEY = "username";
const SESSION_KEY = "session";
const SSO_TEST_PENDING_KEY = "sso-test-pending";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [username, setUsername] = useState<string | null>(() =>
    localStorage.getItem(USERNAME_KEY),
  );

  useEffect(() => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      if (isTokenExpired(token)) {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (refreshToken) {
          // Access token expired but refresh token is available.
          // Stay authenticated — fetchClient's proactive refresh interceptor
          // will obtain a fresh access token on the next API call.
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem(ACCESS_TOKEN_KEY);
          localStorage.removeItem(USERNAME_KEY);
          localStorage.removeItem(SESSION_KEY);
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(true);
      }
    }
    setIsAuthChecked(true);
  }, []);

  // Cross-tab sync + forced logout from fetchClient on token refresh failure.
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === ACCESS_TOKEN_KEY) {
        setIsAuthenticated(!!e.newValue);
      }
    };
    const handleUnauthorized = () => {
      setIsAuthenticated(false);
      setUsername(null);
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("auth:unauthorized", handleUnauthorized);
    };
  }, []);

  const login = useCallback(
    async (username: string, password: string): Promise<LoginResponse> => {
      setIsLoading(true);
      try {
        const response = await authApi.login({ username, password });
        if (response.session) {
          localStorage.setItem(SESSION_KEY, response.session);
        }
        if (response.success && response.accessToken && response.refreshToken) {
          localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
          localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
          localStorage.setItem(USERNAME_KEY, username);
          localStorage.removeItem(SESSION_KEY);
          setIsAuthenticated(true);
          setUsername(username);
        }
        return response;
      } catch (error) {
        Logger.error("Login failed:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const verifyMfa = useCallback(
    async (
      username: string,
      mfaCode: string,
      session: string,
      challengeName: string,
    ): Promise<LoginResponse> => {
      setIsLoading(true);
      try {
        const response = await authApi.verifyMfa({
          username,
          mfaCode,
          session,
          challengeName: challengeName as "SMS_MFA" | "SOFTWARE_TOKEN_MFA",
        });
        if (response.success && response.accessToken && response.refreshToken) {
          localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
          localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
          localStorage.setItem(USERNAME_KEY, username);
          localStorage.removeItem(SESSION_KEY);
          setIsAuthenticated(true);
          setUsername(username);
        }
        return response;
      } catch (error) {
        Logger.error("MFA verification failed:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const resetPassword = useCallback(
    async (
      username: string,
      newPassword: string,
      session: string,
    ): Promise<ResetPasswordResponse> => {
      setIsLoading(true);
      try {
        const response = await authApi.resetPassword({
          username,
          newPassword,
          session,
        });
        if (response.session) {
          localStorage.setItem(SESSION_KEY, response.session);
        }
        return response;
      } catch (error) {
        Logger.error("Password reset failed:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const setupMfa = useCallback(
    async (username: string, session: string): Promise<SetupMfaResponse> => {
      setIsLoading(true);
      try {
        const response = await authApi.setupMfa({
          username,
          session,
          mfaType: "TOTP",
        });
        if (response.session) {
          localStorage.setItem(SESSION_KEY, response.session);
        }
        return response;
      } catch (error) {
        Logger.error("MFA setup failed:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const confirmMfa = useCallback(
    async (
      username: string,
      mfaCode: string,
      session: string,
    ): Promise<ConfirmMfaResponse> => {
      setIsLoading(true);
      try {
        const response = await authApi.confirmMfa({
          username,
          mfaCode,
          session,
          mfaType: "TOTP",
        });
        if (response.success && response.accessToken && response.refreshToken) {
          localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
          localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
          localStorage.setItem(USERNAME_KEY, username);
          localStorage.removeItem(SESSION_KEY);
          // Do NOT set isAuthenticated here — caller shows success step first,
          // then calls completeAuth() before navigating.
          setUsername(username);
        }
        return response;
      } catch (error) {
        Logger.error("MFA confirmation failed:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const completeAuth = useCallback(() => {
    setIsAuthenticated(true);
  }, []);

  const forgotPassword = useCallback(
    async (username: string): Promise<ForgotPasswordResponse> => {
      return authApi.forgotPassword({ username });
    },
    [],
  );

  const confirmForgotPassword = useCallback(
    async (
      username: string,
      confirmationCode: string,
      newPassword: string,
    ): Promise<ConfirmForgotPasswordResponse> => {
      return authApi.confirmForgotPassword({
        username,
        confirmationCode,
        newPassword,
      });
    },
    [],
  );

  const loginWithSSO = useCallback(
    async (code: string, state: string): Promise<LoginResponse> => {
      setIsLoading(true);
      try {
        const response = await authApi.ssoCallback({ code, state });
        if (response.accessToken && response.refreshToken) {
          localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
          localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
          const pendingEmail = sessionStorage.getItem("sso_pending_email");
          sessionStorage.removeItem("sso_pending_email");
          const payload = decodeJwtPayload(response.accessToken);
          const resolvedUsername =
            pendingEmail ||
            (payload?.email as string) ||
            (payload?.username as string) ||
            "";
          if (resolvedUsername) {
            localStorage.setItem(USERNAME_KEY, resolvedUsername);
            setUsername(resolvedUsername);
          }
          setIsAuthenticated(true);
        }
        return response;
      } catch (error) {
        Logger.error("SSO callback failed:", error);
        sessionStorage.removeItem("sso_pending_email");
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    let redirectHref = "/login";
    try {
      const response = await authApi.logout();
      if (response.logoutUrl) {
        try {
          const parsed = new URL(response.logoutUrl);
          if (parsed.protocol === "https:") {
            redirectHref = encodeURI(parsed.toString());
          }
        } catch {
          // malformed URL — fall back to /login
        }
      }
    } catch (error) {
      Logger.error("Logout failed:", error);
    } finally {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USERNAME_KEY);
      localStorage.removeItem(SESSION_KEY);
      localStorage.removeItem(SSO_TEST_PENDING_KEY);
      window.location.href = redirectHref;
    }
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated,
      isAuthChecked,
      isLoading,
      userEmail: username,
      completeAuth,
      login,
      verifyMfa,
      resetPassword,
      setupMfa,
      confirmMfa,
      forgotPassword,
      confirmForgotPassword,
      loginWithSSO,
      logout,
    }),
    [
      isAuthenticated,
      isAuthChecked,
      isLoading,
      username,
      completeAuth,
      login,
      verifyMfa,
      resetPassword,
      setupMfa,
      confirmMfa,
      forgotPassword,
      confirmForgotPassword,
      loginWithSSO,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
